"use client";

import { Loader2, Mail } from "lucide-react";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { AppleSignInButton } from "@/components/shared/AppleSignInButton";
import { GoogleSignInButton } from "@/components/shared/GoogleSignInButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "@/i18n/navigation";

const emailSchema = z.string().email();

interface SignInFormProps {
  callbackUrl?: string;
  hideDisclaimer?: boolean;
}

export function SignInForm({ callbackUrl, hideDisclaimer }: SignInFormProps) {
  const t = useTranslations("signIn");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<"google" | "apple" | "email" | null>(null);
  const [target, setTarget] = useState(callbackUrl || "/");

  useEffect(() => {
    if (!callbackUrl) {
      setTarget(window.location.pathname);
    }
  }, [callbackUrl]);

  const isValidEmail = useMemo(() => emailSchema.safeParse(email).success, [email]);

  const handleOAuth = async (provider: "google" | "apple") => {
    if (busy) return;
    setBusy(provider);
    await signIn(provider, { callbackUrl: target });
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !isValidEmail) return;
    setBusy("email");
    try {
      const res = await signIn("nodemailer", {
        email: email.trim(),
        callbackUrl: target,
        redirect: false,
      });
      if (res?.error) {
        toast.error(t("emailError"));
        setBusy(null);
        return;
      }
      window.location.href = `/auth/verify-request?email=${encodeURIComponent(email)}`;
    } catch {
      toast.error(t("emailError"));
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <GoogleSignInButton
        onClick={() => handleOAuth("google")}
        isLoading={busy === "google"}
        disabled={busy !== null}
      />
      <AppleSignInButton
        onClick={() => handleOAuth("apple")}
        isLoading={busy === "apple"}
        disabled={busy !== null}
      />

      <div className="my-2 flex items-center gap-3 text-sm text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        {t("or")}
        <span className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={handleEmail} className="flex flex-col gap-3">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("emailPlaceholder")}
          required
          disabled={busy !== null}
        />
        <Button
          type="submit"
          variant="outline"
          disabled={busy !== null || !isValidEmail}
          className="w-full justify-center gap-2 h-11"
        >
          {busy === "email" ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Mail className="size-4" aria-hidden />
          )}
          {t("withEmail")}
        </Button>
      </form>

      {!hideDisclaimer && (
        <p className="mt-1 text-center text-xs text-muted-foreground">
          {t("disclaimer")}{" "}
          <Link href="/privacy" className="underline hover:text-foreground">
            {t("privacy")}
          </Link>
          {" · "}
          <Link href="/terms" className="underline hover:text-foreground">
            {t("terms")}
          </Link>
        </p>
      )}
    </div>
  );
}
